import type { FieldArrayWithId } from 'react-hook-form';
import type { AudienceForm, FormSegment } from '../../../../../../../modules/audiences/AudienceForm/types';
import { audienceLocation, type SelectedLocationSegment } from '../../constants';
import type { CriteriaFieldName } from '../groups/GroupList';

export type SelectedLocationInitialState = Record<string, SelectedLocationSegment | undefined>;

type LocationFieldItem = {
  location?: {
    segments?: FormSegment[];
  };
};

const hasLocationProperties = (field: unknown): field is LocationFieldItem => {
  return field !== null && typeof field === 'object' && 'location' in field;
};

const detectLocationSegmentType = (segments: FormSegment[] | undefined): SelectedLocationSegment => {
  if (!segments || segments.length === 0) {
    return audienceLocation.Any;
  }

  const hasState = segments.some(seg => seg.sub_category === 'state');
  const hasZip = segments.some(seg => seg.sub_category === 'zip');
  const hasDma = segments.some(seg => seg.sub_category === 'dma');
  const hasCongressional = segments.some(seg => seg.sub_category === 'congressional');

  if (hasState) {
    return audienceLocation.State;
  }

  if (hasZip) {
    return audienceLocation.ZipCode;
  }

  if (hasDma) {
    return audienceLocation.DMA;
  }

  if (hasCongressional) {
    return audienceLocation.CongressionalDistrict;
  }

  return audienceLocation.Any;
};

export const generateInitialSelectedLocationState = (
  fields: FieldArrayWithId<AudienceForm, `${CriteriaFieldName}.groups`, 'id'>[],
  prefix = ''
): SelectedLocationInitialState => {
  const state: SelectedLocationInitialState = {};

  fields.forEach((fieldItem, index) => {
    const sectionPrefix = prefix ? `${prefix}-` : '';

    // Check if field has location property
    if (!hasLocationProperties(fieldItem)) {
      // No location property means create mode or no location data - set to Any
      state[`${sectionPrefix}${index}`] = audienceLocation.Any;
      return;
    }

    const locationSegments = fieldItem.location?.segments;

    // Detect the type from the segments and set it
    const detectedType = detectLocationSegmentType(locationSegments);
    state[`${sectionPrefix}${index}`] = detectedType;
  });

  return state;
};
